import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sparkles, ShoppingBag, Cpu, Download, Trash2, Power, PowerOff, RefreshCw, Star, Wrench, CheckCircle, Globe, Search, Zap, Tag, Upload, Palette, Terminal, Monitor, Film, Key, ExternalLink, Github, AlertTriangle, X, ShieldCheck, Info, Mail, QrCode, Link, Image, FileText, CloudSun, Languages, Calculator, StickyNote, Timer, TrendingUp, Music, GraduationCap, BriefcaseBusiness, Stethoscope, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useSocket } from '@/hooks/useSocket';
import { getSavedKeyStatus, saveServerKeys } from '@/services/settingsKeys';
import { apiFetch } from '@/services/apiClient';
import { MCPSettings } from './MCPSettings';

const ICON_CLASSES: Record<string, string> = {
  CloudSun: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  Languages: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  Calculator: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  StickyNote: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  Timer: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  Globe: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Mail: 'bg-teal-500/10 text-teal-400 border-teal-500/20',
  QrCode: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  Key: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  Link: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  Image: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
  FileText: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  Palette: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  Sparkles: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  ShoppingBag: 'bg-lime-500/10 text-lime-400 border-lime-500/20',
  ShieldCheck: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  Wrench: 'bg-slate-500/10 text-slate-300 border-slate-500/20',
  GraduationCap: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  BriefcaseBusiness: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  Stethoscope: 'bg-red-500/10 text-red-400 border-red-500/20',
  Terminal: 'bg-green-500/10 text-green-400 border-green-500/20',
  Monitor: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  Search: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  Film: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
};

const ICON_COMPONENTS: Record<string, React.ReactNode> = {
  Calculator: <Calculator size={18} />,
  CloudSun: <CloudSun size={18} />,
  FileText: <FileText size={18} />,
  Globe: <Globe size={18} />,
  Image: <Image size={18} />,
  Languages: <Languages size={18} />,
  Link: <Link size={18} />,
  Mail: <Mail size={18} />,
  Music: <Music size={18} />,
  QrCode: <QrCode size={18} />,
  ShieldCheck: <ShieldCheck size={18} />,
  Sparkles: <Sparkles size={18} />,
  ShoppingBag: <ShoppingBag size={18} />,
  GraduationCap: <GraduationCap size={18} />,
  BriefcaseBusiness: <BriefcaseBusiness size={18} />,
  Stethoscope: <Stethoscope size={18} />,
  StickyNote: <StickyNote size={18} />,
  Terminal: <Terminal size={18} />,
  Timer: <Timer size={18} />,
  TrendingUp: <TrendingUp size={18} />,
  Monitor: <Monitor size={18} />,
  Search: <Search size={18} />,
  Palette: <Palette size={18} />,
  Film: <Film size={18} />,
  Wrench: <Wrench size={18} />,
  Zap: <Zap size={18} />,
};

interface MarketplaceSkill {
  id: string; name: string; description: string; author: string;
  downloads: number; rating: number; category: string; icon: string;
  installSource?: 'bundled' | 'community' | 'npm' | 'github'; installPath?: string; installed: boolean;
  version?: string; toolCount?: number;
  requiresApiKey?: boolean; apiKeyEnv?: string; apiKeyUrl?: string;
  requiresSetup?: boolean; setupNote?: string;
  npmPackage?: string; repoUrl?: string;
  runtime?: 'internal' | 'external'; externalCommand?: string;
  externalAgentId?: string; externalHealthStatus?: string;
}

interface InstalledSkill {
  name: string; description: string; enabled: boolean; source: string;
  autoGenerated: boolean; toolCount: number; installedAt: string; connected: boolean;
  broken?: boolean; healthStatus?: string; consecutiveCrashes?: number; lastCrashTime?: string;
  startupError?: string;
}

type SkillAvailability = {
  label: string;
  详情: string;
  tone: 'green' | 'amber' | 'cyan' | 'red' | 'white';
  installedSkill?: InstalledSkill;
};

const AVAILABILITY_CLASSES: Record<SkillAvailability['tone'], string> = {
  green: 'bg-green-500/10 text-green-300 border-green-500/15',
  amber: 'bg-amber-500/10 text-amber-300 border-amber-500/15',
  cyan: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/15',
  red: 'bg-red-500/10 text-red-300 border-red-500/15',
  white: 'bg-white/[0.055] text-white/55 border-white/[0.08]',
};

function normalizeSkillKey(value?: string): string {
  return String(value || '')
    .toLowerCase()
    .replace(/^skill-/, '')
    .replace(/^lumi official:\s*/, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function findInstalledSkillForMarket(skill: MarketplaceSkill, installedSkills: InstalledSkill[]): InstalledSkill | undefined {
  const candidates = new Set([
    normalizeSkillKey(skill.id),
    normalizeSkillKey(skill.name),
    normalizeSkillKey(skill.id.replace(/^skill-/, '')),
  ]);
  return installedSkills.find(installed => candidates.has(normalizeSkillKey(installed.name)));
}

function getSkillAvailability(
  skill: MarketplaceSkill,
  installedSkills: InstalledSkill[],
  savedKeys: Record<string, boolean>,
  lang: 'en' | 'zh',
): SkillAvailability {
  const installedSkill = findInstalledSkillForMarket(skill, installedSkills);
  if (installedSkill?.broken) {
    return {
      label: lang === 'zh' ? '需修复' : 'Needs repair',
      详情: lang === 'zh' ? '本地技能包不完整，修复后才能调用。' : 'Local package is incomplete and needs repair before use.',
      tone: 'red',
      installedSkill,
    };
  }
  if (installedSkill?.startupError || (installedSkill?.enabled && !installedSkill.connected)) {
    return {
      label: lang === 'zh' ? '待连接' : 'Needs connection',
      详情: installedSkill.startupError || (lang === 'zh' ? '已安装但 MCP 进程尚未连接。' : 'Installed but its MCP server is not connected yet.'),
      tone: 'amber',
      installedSkill,
    };
  }
  if (installedSkill?.connected) {
    return {
      label: lang === 'zh' ? '可调用' : 'Callable',
      详情: lang === 'zh' ? '已连接到 小枫，聊天、运行日志和工具调度可使用。' : 'Connected to 小枫 and available to chat, run logs, and tool routing.',
      tone: 'green',
      installedSkill,
    };
  }
  if (installedSkill && !installedSkill.enabled) {
    return {
      label: lang === 'zh' ? '已安装未启用' : 'Installed, disabled',
      详情: lang === 'zh' ? '技能已在本机，但当前被关闭。' : 'The skill exists locally, but is currently disabled.',
      tone: 'white',
      installedSkill,
    };
  }
  if (skill.runtime === 'external') {
    return {
      label: lang === 'zh' ? '外部代理' : 'External agent',
      详情: lang === 'zh' ? '需要本机先安装对应 CLI，安装后会作为团队成员接入。' : 'Requires the matching CLI on this computer, then connects as a team agent.',
      tone: 'cyan',
    };
  }
  if (skill.requiresApiKey && skill.apiKeyEnv && !savedKeys[skill.apiKeyEnv]) {
    return {
      label: lang === 'zh' ? '需 API Key' : 'Needs API key',
      详情: lang === 'zh' ? `安装后需配置 ${skill.apiKeyEnv} 才能真实调用。` : `Configure ${skill.apiKeyEnv} after install before real calls can run.`,
      tone: 'amber',
    };
  }
  if (skill.requiresSetup) {
    return {
      label: lang === 'zh' ? '需本机环境' : 'Needs setup',
      详情: skill.setupNote || (lang === 'zh' ? '需要先完成本机依赖安装。' : 'Requires local dependencies before use.'),
      tone: 'amber',
    };
  }
  return {
    label: lang === 'zh' ? '安装后可用' : 'Ready after install',
    详情: lang === 'zh' ? '官方内置技能，安装并连接后可直接调用。' : 'Official bundled skill. Install and connect it to call it.',
    tone: 'green',
  };
}

function getDisplayAvailability(
  skill: MarketplaceSkill,
  installedSkills: InstalledSkill[],
  savedKeys: Record<string, boolean>,
  lang: 'en' | 'zh',
): SkillAvailability {
  const installedSkill = findInstalledSkillForMarket(skill, installedSkills);
  if (installedSkill?.broken) {
    return {
      label: lang === 'zh' ? '需修复' : 'Needs repair',
      详情: lang === 'zh' ? '本地技能包不完整，修复后才能调用。' : 'Local package is incomplete and needs repair before use.',
      tone: 'red',
      installedSkill,
    };
  }
  if (installedSkill?.startupError || (installedSkill?.enabled && !installedSkill.connected)) {
    return {
      label: lang === 'zh' ? '已安装未连接' : 'Installed, not connected',
      详情: installedSkill.startupError || (lang === 'zh' ? '已安装，但本地服务还没有连上 小枫。' : 'Installed, but its local service is not connected to 小枫 yet.'),
      tone: 'amber',
      installedSkill,
    };
  }
  if (installedSkill?.connected) {
    return {
      label: lang === 'zh' ? '可调用' : 'Callable',
      详情: lang === 'zh' ? '已连接到 小枫，可以在聊天、调度和工具调用中使用。' : 'Connected to 小枫 and available for chat, orchestration, and tool calls.',
      tone: 'green',
      installedSkill,
    };
  }
  if (installedSkill && !installedSkill.enabled) {
    return {
      label: lang === 'zh' ? '已安装未启用' : 'Installed, disabled',
      详情: lang === 'zh' ? '技能已在本机，但当前被关闭。' : 'The skill exists locally, but is currently disabled.',
      tone: 'white',
      installedSkill,
    };
  }
  if (skill.installed) {
    if (skill.runtime === 'external' && skill.externalHealthStatus === 'online') {
      return {
        label: lang === 'zh' ? '可调用' : 'Callable',
        详情: lang === 'zh' ? '已在 Team 且连接测试通过，小枫 可以把任务调度给它。' : 'In Team with a passing connection test, so 小枫 can schedule work to it.',
        tone: 'green',
      };
    }
    return {
      label: lang === 'zh' ? '已安装未连接' : 'Installed, not connected',
      详情: skill.runtime === 'external'
        ? (lang === 'zh' ? '已加入 Team，需要在 Team 里通过连接测试后才会参与调度。' : 'Added to Team. Run a Team connection test before 小枫 schedules it.')
        : (lang === 'zh' ? '已安装，但当前连接状态还没变为可调用。' : 'Installed, but it has not reported a callable connection yet.'),
      tone: 'amber',
    };
  }
  if (skill.runtime === 'external') {
    return {
      label: lang === 'zh' ? '外部 Agent' : 'External Agent',
      详情: lang === 'zh' ? '安装会把它加入 Team，通过本机 CLI 桥接，连接测试通过后再参与调度。' : 'Install adds it to Team as a local CLI-backed agent, then a health test enables orchestration.',
      tone: 'cyan',
    };
  }
  if ((skill.requiresApiKey && skill.apiKeyEnv && !savedKeys[skill.apiKeyEnv]) || skill.requiresSetup) {
    return {
      label: lang === 'zh' ? '需配置' : 'Needs configuration',
      详情: skill.requiresApiKey && skill.apiKeyEnv
        ? (lang === 'zh' ? `需要配置 ${skill.apiKeyEnv} 后才能真实调用。` : `Configure ${skill.apiKeyEnv} before real calls can run.`)
        : (skill.setupNote || (lang === 'zh' ? '需要先完成本机依赖或账号配置。' : 'Requires local dependencies or account setup before use.')),
      tone: 'amber',
    };
  }
  return {
    label: lang === 'zh' ? '仅可安装' : 'Installable',
    详情: lang === 'zh' ? '当前只是大厅里的可安装技能；安装并连接后 小枫 才能调用。' : 'Available in the hall only; 小枫 can call it after install and connection.',
    tone: 'white',
  };
}

function getInstalledSkillAvailability(skill: InstalledSkill, lang: 'en' | 'zh'): SkillAvailability {
  if (skill.broken) {
    return {
      label: lang === 'zh' ? '需修复' : 'Needs repair',
      详情: lang === 'zh' ? '本地技能包不完整，修复后才能调用。' : 'Local package is incomplete and needs repair before use.',
      tone: 'red',
      installedSkill: skill,
    };
  }
  if (skill.startupError || (skill.enabled && !skill.connected)) {
    return {
      label: lang === 'zh' ? '已安装未连接' : 'Installed, not connected',
      详情: skill.startupError || (lang === 'zh' ? '本地服务还没有连上 小枫。' : 'The local service is not connected to 小枫 yet.'),
      tone: 'amber',
      installedSkill: skill,
    };
  }
  if (skill.connected) {
    return {
      label: lang === 'zh' ? '可调用' : 'Callable',
      详情: lang === 'zh' ? '已连接到 小枫，可以参与工具调用。' : 'Connected to 小枫 and available for tool calls.',
      tone: 'green',
      installedSkill: skill,
    };
  }
  return {
    label: lang === 'zh' ? '已安装未启用' : 'Installed, disabled',
    详情: lang === 'zh' ? '技能已在本机，但当前被关闭。' : 'The skill exists locally, but is currently disabled.',
    tone: 'white',
    installedSkill: skill,
  };
}

function isExternalRuntimeSkill(skill?: Partial<MarketplaceSkill | ExternalResult>): boolean {
  return Boolean(skill && 'runtime' in skill && skill.runtime === 'external');
}

function getInstallActionText(skill: Partial<MarketplaceSkill | ExternalResult>, installed: boolean, lang: 'en' | 'zh', t: any): string {
  if (installed) return isExternalRuntimeSkill(skill) ? (lang === 'zh' ? '已在 Team' : '已在 Team') : (t.installedStatus || '已安装');
  return isExternalRuntimeSkill(skill) ? (lang === 'zh' ? '加入 Team' : '加入 Team') : (t.installBtn || '安装');
}

function SkillStatusCallout({ availability, compact = false }: { availability: SkillAvailability; compact?: boolean }) {
  const icon = availability.tone === 'green'
    ? <CheckCircle size={compact ? 11 : 13} />
    : availability.tone === 'cyan'
      ? <ExternalLink size={compact ? 11 : 13} />
      : availability.tone === 'amber' || availability.tone === 'red'
        ? <AlertTriangle size={compact ? 11 : 13} />
        : <ShieldCheck size={compact ? 11 : 13} />;
  return (
    <div className={`rounded-xl border ${compact ? 'px-3 py-2' : 'px-3.5 py-3'} ${AVAILABILITY_CLASSES[availability.tone]}`}>
      <div className="flex items-center gap-1.5 text-[12px] font-black uppercase">
        {icon}
        <span>{availability.label}</span>
      </div>
      <p className={`${compact ? 'line-clamp-1 text-[11px]' : 'text-xs'} mt-1 leading-relaxed opacity-75`}>
        {availability.详情}
      </p>
    </div>
  );
}

/** Normalized external result from npm or GitHub */
interface ExternalResult {
  id: string; name: string; description: string; author: string;
  installSource: 'npm' | 'github'; source: string;
  version?: string; stars?: number; url?: string; language?: string;
  npmPackage?: string; repoUrl?: string;
}

export type SkillCenterTab = 'featured' | 'marketplace' | 'installed' | 'mcp' | 'generate';
type SortKey = 'downloads' | 'rating' | 'newest';

interface ProfessionProfile {
  profession: string;
  confidence?: number;
  evidence?: string[];
  knowledgeDomains?: string[];
}

interface SkillRecommendation {
  skill: MarketplaceSkill;
  score: number;
  reasons: string[];
}

const PROFESSION_RECOMMENDATION_MAP: Record<string, string[]> = {
  teacher: ['skill-education-teacher', 'skill-pdftools', 'skill-translator', 'skill-notes'],
  doctor: ['skill-medical-admin', 'skill-pdftools', 'skill-fetcher'],
  lawyer: ['skill-legal-casework', 'skill-pdftools', 'skill-web-scraper'],
  designer: ['skill-design-studio-pack', 'skill-image', 'skill-video-editor'],
  engineer: ['skill-code-sandbox', 'skill-deep-crawler', 'skill-fetcher'],
  executive: ['skill-executive-ops', 'skill-finance-office', 'skill-sales-customer-ops'],
  founder: ['skill-executive-ops', 'skill-finance-office', 'skill-ecommerce-ops'],
  owner: ['skill-executive-ops', 'skill-finance-office', 'skill-sales-customer-ops'],
  manager: ['skill-executive-ops', 'skill-hr-recruiting', 'skill-sales-customer-ops'],
  ecommerce: ['skill-ecommerce-ops', 'skill-finance-office', 'skill-sales-customer-ops'],
  finance: ['skill-finance-office', 'skill-executive-ops'],
  accountant: ['skill-finance-office', 'skill-pdftools'],
  recruiter: ['skill-hr-recruiting', 'skill-pdftools'],
  sales: ['skill-sales-customer-ops', 'skill-executive-ops'],
  restaurant: ['skill-restaurant-store-ops', 'skill-finance-office'],
  trader: ['skill-cross-border-trade', 'skill-ecommerce-ops', 'skill-translator'],
  exporter: ['skill-cross-border-trade', 'skill-translator', 'skill-finance-office'],
  manufacturer: ['skill-manufacturing-qa', 'skill-finance-office'],
  factory: ['skill-manufacturing-qa', 'skill-executive-ops'],
  quality: ['skill-manufacturing-qa'],
  realtor: ['skill-property-ops', 'skill-cad-drafting', 'skill-legal-casework'],
  property: ['skill-property-ops', 'skill-cad-drafting'],
  insurance: ['skill-insurance-advisor', 'skill-sales-customer-ops'],
  creator: ['skill-content-ops', 'skill-design-studio-pack', 'skill-video-editor'],
  marketer: ['skill-content-ops', 'skill-sales-customer-ops'],
  product: ['skill-product-project-ops', 'skill-design-studio-pack'],
  project: ['skill-product-project-ops', 'skill-executive-ops'],
  assistant: ['skill-admin-assistant', 'skill-pdftools', 'skill-email-assistant'],
  admin: ['skill-admin-assistant', 'skill-pdftools', 'skill-notes'],
  procurement: ['skill-procurement-supply-chain', 'skill-finance-office'],
  supply: ['skill-procurement-supply-chain', 'skill-logistics-warehouse'],
  analyst: ['skill-data-bi', 'skill-finance-office'],
  data: ['skill-data-bi', 'skill-code-sandbox'],
  logistics: ['skill-logistics-warehouse', 'skill-cross-border-trade'],
  warehouse: ['skill-logistics-warehouse', 'skill-ecommerce-ops'],
  streamer: ['skill-live-commerce', 'skill-content-ops', 'skill-video-editor'],
  construction: ['skill-construction-tender-cost', 'skill-cad-drafting'],
  estimator: ['skill-construction-tender-cost', 'skill-finance-office'],
};

const WORK_RECOMMENDATION_RULES: Array<{ skillIds: string[]; zh: string; en: string; pattern: RegExp }> = [
  { skillIds: ['skill-executive-ops'], zh: '经营/管理', en: 'management work', pattern: /企业负责人|老板|创始人|CEO|总经理|管理|经营|KPI|OKR|会议纪要|决策|现金跑道|runway|executive|founder|manager|decision/i },
  { skillIds: ['skill-ecommerce-ops', 'skill-finance-office'], zh: '电商/平台经营', en: 'e-commerce work', pattern: /电商|店铺|SKU|库存|平台结算|淘宝|抖店|小红书|shopify|ecommerce|marketplace|listing|settlement/i },
  { skillIds: ['skill-cross-border-trade', 'skill-ecommerce-ops', 'skill-translator'], zh: '外贸/跨境', en: 'foreign trade or cross-border commerce', pattern: /外贸|跨境|询盘|报价单|报关|清关|海关|关税|货代|提单|信用证|FOB|CIF|DDP|incoterm|customs|tariff|freight|forwarder|export|import|cross.?border|foreign trade/i },
  { skillIds: ['skill-manufacturing-qa'], zh: '制造/工厂/质检', en: 'manufacturing or quality work', pattern: /制造|工厂|生产|产线|质检|品控|BOM|物料|供应商|交期|8D|不良|返工|报废|work order|production|factory|quality|supplier|defect|inspection/i },
  { skillIds: ['skill-property-ops', 'skill-cad-drafting'], zh: '房产/物业/装修', en: 'property or renovation work', pattern: /房产|房源|中介|物业|租赁|看房|业主|租客|工单|装修|施工进度|预算|材料清单|real estate|property|leasing|tenant|landlord|renovation/i },
  { skillIds: ['skill-insurance-advisor', 'skill-sales-customer-ops'], zh: '保险/顾问', en: 'insurance advisory work', pattern: /保险|保单|投保|续保|理赔|保障|重疾|寿险|车险|年金|客户画像|insurance|policy|claim|renewal|premium|coverage/i },
  { skillIds: ['skill-content-ops', 'skill-design-studio-pack', 'skill-video-editor'], zh: '新媒体/内容运营', en: 'content or creator operations', pattern: /新媒体|内容运营|短视频|选题|脚本|账号复盘|评论分析|小红书|抖音|视频号|公众号|直播脚本|content|creator|tiktok|youtube|script|calendar/i },
  { skillIds: ['skill-product-project-ops', 'skill-executive-ops'], zh: '产品/项目管理', en: 'product or project management', pattern: /产品经理|项目经理|PRD|需求池|需求文档|用户故事|验收标准|路线图|排期|里程碑|迭代|sprint|roadmap|backlog|user story|acceptance criteria|project manager|product manager/i },
  { skillIds: ['skill-admin-assistant', 'skill-pdftools', 'skill-email-assistant'], zh: '行政/助理', en: 'administration or assistant work', pattern: /行政|助理|老板助理|总助|日程|会议安排|报销|采购申请|通知|资料归档|档案|admin|assistant|schedule|reimbursement|filing/i },
  { skillIds: ['skill-procurement-supply-chain', 'skill-finance-office'], zh: '采购/供应链', en: 'procurement or supply chain work', pattern: /采购|供应链|供应商|询价|比价|采购计划|交期风险|库存预警|对账|采购合同|procurement|supply chain|vendor|supplier|purchase order|PO/i },
  { skillIds: ['skill-data-bi', 'skill-finance-office'], zh: '数据分析/BI', en: 'data analysis or BI work', pattern: /数据分析|BI|报表|看板|指标口径|数据清洗|CSV|Excel|异常解释|周报|月报|dashboard|metric|analytics|anomaly|reporting/i },
  { skillIds: ['skill-logistics-warehouse', 'skill-ecommerce-ops'], zh: '物流/仓储', en: 'logistics or warehouse work', pattern: /物流|仓储|仓库|入库|出库|拣货|盘点|配送|运费|库存差异|快递|carrier|warehouse|logistics|inbound|outbound|picking|freight/i },
  { skillIds: ['skill-live-commerce', 'skill-content-ops', 'skill-ecommerce-ops'], zh: '直播运营/带货', en: 'live commerce work', pattern: /直播|主播|带货|直播间|货盘|场控|直播脚本|转化复盘|GMV|直播售后|live commerce|livestream|host script|rundown/i },
  { skillIds: ['skill-construction-tender-cost', 'skill-cad-drafting', 'skill-legal-casework'], zh: '建筑/造价/招投标', en: 'construction tendering or cost work', pattern: /建筑|工程|造价|招投标|投标|清单|工程量|标书|施工节点|风险条款|BOQ|tender|bid|quantity survey|construction|cost estimate/i },
  { skillIds: ['skill-finance-office'], zh: '财务/税务', en: 'finance or tax work', pattern: /财务|财税|税务|发票|现金流|预算|应收|应付|账龄|invoice|cashflow|tax|vat|receivable|payable/i },
  { skillIds: ['skill-education-teacher'], zh: '教学/教培', en: 'teaching work', pattern: /老师|教师|教培|教学|教案|备课|作业|评分|学生|家长|teacher|lesson|student|rubric/i },
  { skillIds: ['skill-medical-admin'], zh: '医疗文书/随访', en: 'medical documentation work', pattern: /医生|医疗|病历|问诊|随访|患者|检查报告|clinical|medical|patient|follow.?up/i },
  { skillIds: ['skill-hr-recruiting'], zh: '招聘/人事', en: 'HR or recruiting work', pattern: /HR|人事|招聘|简历|面试|候选人|入职|recruit|resume|candidate|interview|onboarding/i },
  { skillIds: ['skill-sales-customer-ops'], zh: '销售/客服', en: 'sales or support work', pattern: /销售|客服|客户|线索|跟进|异议|工单|续费|lead|sales|customer|support|objection/i },
  { skillIds: ['skill-restaurant-store-ops'], zh: '餐饮/门店', en: 'restaurant or store work', pattern: /餐饮|门店|咖啡店|菜单|毛利|报损|排班|点评|促销|restaurant|cafe|store|menu|waste|shift/i },
  { skillIds: ['skill-legal-casework'], zh: '法律/案件', en: 'legal work', pattern: /法律|律师|案件|合同|起诉|答辩|court|legal|lawsuit|contract/i },
  { skillIds: ['skill-design-studio-pack'], zh: '设计/品牌', en: 'design work', pattern: /设计|品牌|logo|海报|视觉|UI|UX|design|brand|poster/i },
  { skillIds: ['skill-cad-drafting'], zh: 'CAD/装修图纸', en: 'CAD or drafting work', pattern: /CAD|DXF|图纸|平面图|施工图|装修|floor plan|drafting/i },
];

export function SkillCenter({ t, lang, initialTab = 'featured' }: { t: any; lang: 'en' | 'zh'; initialTab?: SkillCenterTab }) {
  const [activeTab, setActiveTab] = useState<SkillCenterTab>(initialTab);
  const [marketSkills, setMarketSkills] = useState<MarketplaceSkill[]>([]);
  const [installedSkills, setInstalledSkills] = useState<InstalledSkill[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState<string | null>(null);
  const [repairing, setRepairing] = useState<string | null>(null);
  const [cleaningBroken, setCleaningBroken] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('downloads');
  const [genDescription, setGenDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [npmResults, setNpmResults] = useState<ExternalResult[]>([]);
  const [githubResults, setGithubResults] = useState<ExternalResult[]>([]);
  const [searchingExternal, setSearchingExternal] = useState(false);
  const [externalError, setExternalError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [installProgress, setInstallProgress] = useState<{ skillId: string; stage: string } | null>(null);
  const [详情Skill, setDetailSkill] = useState<MarketplaceSkill | InstalledSkill | null>(null);
  const [savedKeys, setSavedKeys] = useState<Record<string, boolean>>({});
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [professionProfiles, setProfessionProfiles] = useState<ProfessionProfile[]>([]);
  const [workSignalText, setWorkSignalText] = useState('');
  const [recommendationError, setRecommendationError] = useState('');
  const socket = useSocket();

  const [translationReady, setTranslationReady] = useState(false);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const fetchMarketplace = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/marketplace/skills?lang=${lang}`);
      if (!res.ok) throw new Error(`Marketplace failed (${res.status})`);
      setMarketSkills(await res.json());
    } catch (err: any) {
      setLoadError(err.message || '市场加载失败');
    }
  }, [lang]);

  const fetchInstalled = useCallback(async () => {
    try {
      const res = await apiFetch('/api/skills');
      if (!res.ok) throw new Error(`Installed skills failed (${res.status})`);
      const data = await res.json();
      setInstalledSkills(data.skills || []);
    } catch (err: any) {
      setLoadError(err.message || '已安装技能加载失败');
    }
    setLoading(false);
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/marketplace/categories?lang=${lang}`);
      if (!res.ok) throw new Error(`Categories failed (${res.status})`);
      setCategories((await res.json()).map((c: any) => c.name));
    } catch (err: any) {
      setLoadError(err.message || '分类加载失败');
    }
  }, [lang]);

  // External search (npm + GitHub) — debounced
  const fetchExternal = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setNpmResults([]); setGithubResults([]); return; }
    setSearchingExternal(true);
    setExternalError('');
    const qClean = encodeURIComponent(q);
    try {
      const [npmRes, ghRes] = await Promise.allSettled([
        apiFetch(`/api/marketplace/discover/npm?q=${qClean}`).then(async r => {
          if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `npm search failed (${r.status})`);
          return r.json();
        }),
        apiFetch(`/api/marketplace/discover/github?topic=${qClean}`).then(async r => {
          if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `GitHub search failed (${r.status})`);
          return r.json();
        }),
      ]);
      setNpmResults((npmRes.status === 'fulfilled' ? npmRes.value?.results || [] : []).slice(0, 6));
      setGithubResults((ghRes.status === 'fulfilled' ? ghRes.value?.results || [] : []).slice(0, 6));
      const errors = [npmRes, ghRes]
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map(r => r.reason?.message)
        .filter(Boolean);
      if (errors.length) setExternalError(errors.join(' / '));
    } catch (err: any) {
      setExternalError(err.message || '外部搜索失败');
    }
    setSearchingExternal(false);
  }, []);

  const fetchSavedKeys = useCallback(async () => {
    try {
      setSavedKeys(await getSavedKeyStatus());
    } catch {}
  }, []);

  const fetchRecommendationSignals = useCallback(async () => {
    setRecommendationError('');
    try {
      const [profileRes, interactionsRes] = await Promise.allSettled([
        apiFetch('/api/explore/profession'),
        apiFetch('/api/interactions?limit=80'),
      ]);

      if (profileRes.status === 'fulfilled' && profileRes.value.ok) {
        const data = await profileRes.value.json().catch(() => ({}));
        setProfessionProfiles(Array.isArray(data.profiles) ? data.profiles : []);
      }

      if (interactionsRes.status === 'fulfilled' && interactionsRes.value.ok) {
        const data = await interactionsRes.value.json().catch(() => []);
        const items = Array.isArray(data) ? data : [];
        const text = items
          .flatMap((item: any) => [item?.content, item?.message, item?.prompt, item?.response, item?.summary, item?.title, item?.description])
          .filter(Boolean)
          .map(value => (typeof value === 'string' ? value : JSON.stringify(value)))
          .join('\n')
          .slice(0, 50000);
        setWorkSignalText(text);
      }
    } catch (err: any) {
      setRecommendationError(err?.message || '推荐信号加载失败');
    }
  }, []);

  useEffect(() => { fetchMarketplace(); fetchInstalled(); fetchCategories(); fetchSavedKeys(); fetchRecommendationSignals(); }, [fetchMarketplace, fetchInstalled, fetchCategories, fetchSavedKeys, fetchRecommendationSignals]);

  // Debounced external search
  useEffect(() => {
    const timer = setTimeout(() => fetchExternal(search), 400);
    return () => clearTimeout(timer);
  }, [search, fetchExternal]);

  // Translate on first Chinese load, then re-fetch to show translated data
  useEffect(() => {
    let cancelled = false;
    if (lang === 'zh' && !translationReady) {
      apiFetch('/api/marketplace/translate?lang=zh', { method: 'POST' })
        .then(() => { if (!cancelled) { setTranslationReady(true); fetchMarketplace(); } })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [lang, translationReady, fetchMarketplace]);

  // Socket events for real-time updates
  useEffect(() => {
    if (!socket) return;
    const refresh = () => { fetchInstalled(); fetchMarketplace(); };
    const onInstalling = (data: { skillId: string; name: string; stage: string }) => {
      setInstallProgress({ skillId: data.skillId, stage: data.stage });
    };
    const onInstalled = () => {
      setInstallProgress(null);
      refresh();
    };
    socket.on('skill:installing', onInstalling);
    socket.on('skill:installed', onInstalled);
    socket.on('skill:uninstalled', refresh);
    socket.on('skill:updated', refresh);
    socket.on('mcp:health_update', refresh);
    return () => {
      socket.off('skill:installing', onInstalling);
      socket.off('skill:installed', onInstalled);
      socket.off('skill:uninstalled', refresh);
      socket.off('skill:updated', refresh);
      socket.off('mcp:health_update', refresh);
    };
  }, [socket, fetchInstalled, fetchMarketplace]);

  const handleInstall = async (skill: MarketplaceSkill | ExternalResult) => {
    if ('installed' in skill && skill.installed) return;
    const externalRuntime = isExternalRuntimeSkill(skill);
    setInstalling(skill.id);
    try {
      const body: any = { skillId: skill.id, skillName: skill.name, installSource: skill.installSource };
      if ('installPath' in skill && skill.installPath) body.installPath = skill.installPath;
      if ('npmPackage' in skill && skill.npmPackage) body.npmPackage = skill.npmPackage;
      if ('repoUrl' in skill && skill.repoUrl) body.repoUrl = skill.repoUrl;
      const res = await apiFetch('/api/marketplace/skills/acquire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      });
      // Bug 39: res.json() 添加 .catch 兜底
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        if (externalRuntime || data.agentId) {
          toast.success(data.message || `"${skill.name}" ${lang === 'zh' ? '已加入 Team' : 'added to Team'}`);
          window.dispatchEvent(new CustomEvent('xf:agents-changed', { detail: { agentId: data.agentId, name: skill.name } }));
          window.dispatchEvent(new CustomEvent('xf:navigate', { detail: { tab: 'team', agentId: data.agentId } }));
        } else {
          toast.success(`"${skill.name}" ${t.skillInstalledToast}`);
        }
        if (socket) socket.emit('skill:installed', { skillId: skill.id, name: skill.name });
        fetchInstalled(); fetchMarketplace();
        // Clear external results after successful install
        setNpmResults(prev => prev.filter(s => s.id !== skill.id));
        setGithubResults(prev => prev.filter(s => s.id !== skill.id));
      } else toast.error(data.error || '安装失败');
    } catch (err: any) { toast.error(err.message); }
    finally { setInstalling(null); }
  };

  const handleUninstall = async (name: string) => {
    try {
      const res = await apiFetch(`/api/skills/${name}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success(`"${name}" ${t.skillUninstalledToast}`);
        if (socket) socket.emit('skill:uninstalled', { name });
        fetchInstalled(); fetchMarketplace();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || '卸载失败');
      }
    } catch (err: any) { toast.error(err.message); }
  };

  const handleRepair = async (name: string) => {
    setRepairing(name);
    try {
      const res = await apiFetch(`/api/skills/${name}/repair`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.reason || data.error || '修复失败');
      toast.success(lang === 'zh' ? `"${name}" 已修复` : `"${name}" repaired`);
      if (socket) socket.emit('skill:updated', { name });
      if (详情Skill && 'name' in 详情Skill && 详情Skill.name === name) setDetailSkill(null);
      fetchInstalled(); fetchMarketplace();
    } catch (err: any) {
      toast.error(err.message || '修复失败');
    } finally {
      setRepairing(null);
    }
  };

  const handleCleanupBroken = async () => {
    setCleaningBroken(true);
    try {
      const res = await apiFetch('/api/skills/broken', { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '清理失败');
      const removed = Array.isArray(data.removed) ? data.removed.length : 0;
      toast.success(lang === 'zh' ? `已清理 ${removed} 个坏技能` : `Cleaned ${removed} broken skills`);
      fetchInstalled(); fetchMarketplace();
    } catch (err: any) {
      toast.error(err.message || '清理失败');
    } finally {
      setCleaningBroken(false);
    }
  };

  const handleToggle = async (name: string, enabled: boolean) => {
    try {
      const res = await apiFetch(`/api/skills/${name}/${enabled ? 'enable' : 'disable'}`, { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '切换失败');
      }
      toast.success(`${enabled ? t.skillEnabledToast : t.skillDisabledToast} ${name}`);
      fetchInstalled();
    } catch (err: any) {
      toast.error(err.message || '切换失败');
    }
  };

  const handleSaveKey = async (envKey: string, value: string) => {
    if (!value.trim()) return;
    setSavingKey(envKey);
    try {
      await saveServerKeys({ [envKey]: value.trim() });
      setSavedKeys(prev => ({ ...prev, [envKey]: true }));
      setKeyInputs(prev => { const n = { ...prev }; delete n[envKey]; return n; });
      toast.success(`${envKey} saved`);
    } catch (err: any) { toast.error(err.message || '保存密钥失败'); }
    finally { setSavingKey(null); }
  };

  const handleGenerate = async () => {
    if (!genDescription.trim()) return;
    setGenerating(true);
    try {
      const res = await apiFetch('/api/skills/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: genDescription }),
        credentials: 'include',
      });
      // Bug 39: res.json() 添加 .catch 兜底
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        toast.success(t.skillGeneratedToast);
        setGenDescription(''); fetchInstalled();
        if (socket) socket.emit('skill:updated', { name: data.skillName });
      } else toast.error(data.error || '生成失败');
    } catch (err: any) { toast.error(err.message); }
    finally { setGenerating(false); }
  };

  const handlePublish = async (skillName: string) => {
    try {
      const installed = installedSkills.find(s => s.name === skillName);
      if (!installed) return;
      const res = await apiFetch('/api/marketplace/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: skillName,
          description: installed.description,
          author: 'Community',
          category: 'Generated',
          toolCount: installed.toolCount,
        }),
      });
      if (res.ok) toast.success(`Published "${skillName}" to community!`);
    } catch {}
  };

  // Filter & sort. External-runtime agents live in Team, not the Skill Hall.
  const skillHallMarketSkills = marketSkills.filter(s => s.runtime !== 'external');
  const skillHallCategories = categories.filter(cat => skillHallMarketSkills.some(s => s.category === cat));
  const installedSkillRows = useMemo(() => (
    installedSkills
      .map(skill => ({
        skill,
        marketSkill: skillHallMarketSkills.find(marketSkill => findInstalledSkillForMarket(marketSkill, [skill])),
      }))
      .sort((a, b) => Number(b.skill.connected) - Number(a.skill.connected) || Number(b.skill.enabled) - Number(a.skill.enabled) || a.skill.name.localeCompare(b.skill.name))
  ), [installedSkills, skillHallMarketSkills]);
  const matchedWorkSignals = useMemo(() => (
    WORK_RECOMMENDATION_RULES
      .filter(rule => rule.pattern.test(workSignalText))
      .slice(0, 6)
  ), [workSignalText]);
  const recommendedSkills = useMemo<SkillRecommendation[]>(() => {
    const byId = new Map<string, MarketplaceSkill>();
    for (const skill of skillHallMarketSkills) byId.set(skill.id, skill);
    const scored = new Map<string, { skill: MarketplaceSkill; score: number; reasons: string[] }>();
    const add = (skillId: string, score: number, reason: string) => {
      const skill = byId.get(skillId);
      if (!skill) return;
      const existing = scored.get(skillId);
      if (existing) {
        existing.score += score;
        if (!existing.reasons.includes(reason)) existing.reasons.push(reason);
      } else {
        scored.set(skillId, { skill, score, reasons: [reason] });
      }
    };

    for (const profile of professionProfiles) {
      const profession = String(profile.profession || '').toLowerCase();
      const confidence = Math.max(0.25, Math.min(1, Number(profile.confidence || 0.45)));
      const skillIds = PROFESSION_RECOMMENDATION_MAP[profession] || [];
      for (const skillId of skillIds) {
        add(
          skillId,
          55 * confidence,
          lang === 'zh'
            ? `Work profile: ${profile.profession} ${Math.round(confidence * 100)}%`
            : `Work profile: ${profile.profession} ${Math.round(confidence * 100)}%`,
        );
      }
    }

    for (const rule of WORK_RECOMMENDATION_RULES) {
      const matcher = new RegExp(rule.pattern.source, 'gi');
      const matches = workSignalText.match(matcher)?.length || 0;
      if (!matches) continue;
      for (const skillId of rule.skillIds) {
        add(
          skillId,
          Math.min(70, 24 + matches * 6),
          lang === 'zh' ? `Recent chats: ${rule.zh}` : `Recent chats: ${rule.en}`,
        );
      }
    }

    for (const { marketSkill, skill } of installedSkillRows) {
      if (!marketSkill) continue;
      add(
        marketSkill.id,
        skill.connected ? 18 : 10,
        lang === 'zh' ? 'Already installed locally' : 'Already installed locally',
      );
    }

    return Array.from(scored.values())
      .sort((a, b) => b.score - a.score || b.skill.downloads - a.skill.downloads)
      .slice(0, 8);
  }, [installedSkillRows, lang, professionProfiles, skillHallMarketSkills, workSignalText]);
  const filteredMarket = skillHallMarketSkills.filter(s => {
    if (search && !s.name.toLowerCase().includes(search.toLowerCase()) && !s.description.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeCategory && s.category !== activeCategory) return false;
    return true;
  });

  const sortedMarket = [...filteredMarket].sort((a, b) => {
    if (sortKey === 'downloads') return b.downloads - a.downloads;
    if (sortKey === 'rating') return b.rating - a.rating;
    return 0;
  });

  const tabs: { id: SkillCenterTab; label: string; icon: React.ReactNode }[] = [
    { id: 'featured', label: t.recommendedTab || (lang === 'zh' ? '\u63a8\u8350' : 'Recommended'), icon: <Sparkles size={14} /> },
    { id: 'marketplace', label: t.marketplaceTab || '市场', icon: <ShoppingBag size={14} /> },
    { id: 'installed', label: t.skillManageTab || (lang === 'zh' ? '\u7ba1\u7406' : 'Manage'), icon: <Cpu size={14} /> },
    { id: 'mcp', label: t.mcp || 'MCP', icon: <Github size={14} /> },
    { id: 'generate', label: t.generateTab || '生成', icon: <Sparkles size={14} /> },
  ];

  const SAMPLE_PROMPTS = [
    t.skillGeneratePlaceholder || '例如：检查网站是否宕机、生成二维码...',
    '', '', '', '',
  ];
  const brokenSkillCount = installedSkills.filter(skill => skill.broken).length;
  const hasSkillRuntimeIssue = (skill: InstalledSkill) =>
    !!skill.broken ||
    !!skill.startupError ||
    (!!skill.healthStatus && ['crashed', 'failed', 'restarting'].includes(skill.healthStatus)) ||
    (skill.enabled && !skill.connected);
  const shouldOfferRepair = (skill: InstalledSkill) => hasSkillRuntimeIssue(skill);
  const recommendationCopy = {
    title: lang === 'zh' ? '\u57fa\u4e8e\u4f60\u7684\u5de5\u4f5c\u63a8\u8350' : 'Recommended for your work',
    subtitle: lang === 'zh'
      ? '\u5c0f\u67ab \u4f1a\u53c2\u8003\u804c\u4e1a\u753b\u50cf\u3001\u6700\u8fd1\u804a\u5929\u548c\u5df2\u5b89\u88c5 Skill\uff1b\u7ee7\u7eed\u548c \u5c0f\u67ab \u804a\u5de5\u4f5c\uff0c\u63a8\u8350\u4f1a\u8d8a\u6765\u8d8a\u51c6\u3002'
      : '小枫 uses the work profile, recent chats, and installed skills. Keep talking about work and this gets sharper.',
    profile: lang === 'zh' ? '\u5de5\u4f5c\u753b\u50cf' : 'Work profile',
    signals: lang === 'zh' ? '\u804a\u5929\u4fe1\u53f7' : 'Chat signals',
    recommendations: lang === 'zh' ? '\u63a8\u8350\u5b89\u88c5' : 'Recommended installs',
    installed: lang === 'zh' ? '\u5df2\u5177\u5907\u7684\u80fd\u529b' : 'Available capabilities',
    installedHint: lang === 'zh'
      ? '\u8fd9\u91cc\u53ea\u505a\u72b6\u6001\u6982\u89c8\uff1b\u542f\u505c\u3001\u4fee\u590d\u3001\u5378\u8f7d\u8bf7\u5230\u7ba1\u7406\u9875\u3002\u5df2\u5b89\u88c5\u7684 Skill \u901a\u5e38\u4f1a\u901a\u8fc7\u672c\u5730 MCP server \u66b4\u9732\u5de5\u5177\u7ed9 \u5c0f\u67ab \u8c03\u7528\u3002'
      : 'This is a status overview. Enable, repair, and uninstall from Manage. Installed skills usually expose tools to 小枫 through a local MCP server.',
    noSignals: lang === 'zh'
      ? '\u6682\u65f6\u8fd8\u6ca1\u6709\u8db3\u591f\u7684\u804c\u4e1a\u6216\u5de5\u4f5c\u4fe1\u53f7\uff0c\u63a8\u8350\u4f1a\u5148\u4fdd\u6301\u4fdd\u5b88\u3002'
      : 'Not enough work signals yet, so recommendations stay conservative.',
    noRecommendations: lang === 'zh'
      ? '\u8fd8\u6ca1\u6709\u660e\u786e\u63a8\u8350\u3002\u548c \u5c0f\u67ab \u804a\u4e00\u804a\u4f60\u7684\u804c\u4e1a\u3001\u5e38\u505a\u7684\u4efb\u52a1\u6216\u5de5\u4f5c\u6d41\u7a0b\u540e\uff0c\u8fd9\u91cc\u4f1a\u81ea\u52a8\u53d8\u5f97\u6709\u7528\u3002'
      : 'No strong recommendations yet. Talk with 小枫 about your profession, recurring tasks, or workflows and this will populate automatically.',
    noInstalled: lang === 'zh' ? '\u8fd8\u6ca1\u6709\u5df2\u5b89\u88c5\u7684 Skill\u3002' : 'No installed skills yet.',
    refresh: lang === 'zh' ? '\u5237\u65b0\u63a8\u8350' : 'Refresh recommendations',
    manage: lang === 'zh' ? '\u7ba1\u7406' : 'Manage',
    managementTitle: lang === 'zh' ? 'Skill / MCP \u7ba1\u7406' : 'Skill / MCP Management',
    managementHint: lang === 'zh'
      ? '\u8fd9\u91cc\u5904\u7406\u672c\u5730 Skill \u5305\u7684\u542f\u7528\u3001\u505c\u7528\u3001\u4fee\u590d\u3001\u53d1\u5e03\u548c\u5378\u8f7d\u3002\u5927\u5385\u548c\u63a8\u8350\u9875\u53ea\u5c55\u793a\u72b6\u6001\u6216\u5b89\u88c5\u5165\u53e3\u3002'
      : 'Enable, disable, repair, publish, and uninstall local Skill packages here. Hall and Recommended only show status or install entry points.',
  };
  const hasRecommendationSignals = professionProfiles.length > 0 || matchedWorkSignals.length > 0 || workSignalText.trim().length > 0;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="xf-panel flex items-center justify-between gap-4 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-celestial-saturn/20 bg-celestial-saturn/10">
            <Zap className="text-celestial-saturn" size={20} />
          </div>
          <h3 className="text-xl font-bold uppercase tracking-tighter text-white/90">{t.skillCenter || '技能中心'}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/45 font-mono">{sortedMarket.length} {t.available || '可用'}</span>
          <button
            onClick={() => { setLoadError(''); fetchMarketplace(); fetchInstalled(); fetchCategories(); fetchRecommendationSignals(); }}
            className="xf-icon-button h-8 w-8"
            title={t.refresh || '刷新'}
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <p className="text-sm text-white/40 max-w-xl">
        {t.skillMarketDesc || 'Browse, install and generate MCP skills. Each skill expands 小枫\'s capabilities with new tools, APIs, and automations. Installed skills appear in your MCP ecosystem.'}
      </p>

      {loadError && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-amber-400/15 bg-amber-500/10 px-4 py-3 text-xs text-amber-100/80">
          <span className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-300" />
            {lang === 'zh' ? `技能大厅加载异常：${loadError}` : `Skill Center load issue: ${loadError}`}
          </span>
          <button
            onClick={() => { setLoadError(''); fetchMarketplace(); fetchInstalled(); fetchCategories(); fetchRecommendationSignals(); }}
            className="rounded-lg bg-amber-300/10 px-3 py-1.5 font-semibold text-amber-200 hover:bg-amber-300/18"
          >
            {lang === 'zh' ? '重试' : 'Retry'}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="xf-panel flex w-fit gap-1 p-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              activeTab === tab.id
                ? 'bg-white/10 text-white'
                : 'text-white/55 hover:bg-white/[0.045] hover:text-white/75'
            }`}
          >
            {tab.icon}
            {tab.label}
            {tab.id === 'installed' && installedSkills.length > 0 && (
              <span className="text-xs px-1.5 py-0.5 bg-white/10 rounded-full text-white/40">{installedSkills.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence>
        {activeTab === 'featured' && (
          <motion.div key="featured" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }} className="space-y-6">
            <div className="xf-panel p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Sparkles size={18} className="text-celestial-saturn" />
                    <h4 className="text-sm font-black uppercase tracking-normal text-white/85">{recommendationCopy.title}</h4>
                  </div>
                  <p className="mt-2 max-w-2xl text-xs leading-relaxed text-white/45">{recommendationCopy.subtitle}</p>
                </div>
                <button
                  onClick={fetchRecommendationSignals}
                  className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.045] px-3 text-xs font-bold text-white/55 transition-colors hover:bg-white/[0.08] hover:text-white/80"
                >
                  <RefreshCw size={13} />
                  {recommendationCopy.refresh}
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {professionProfiles.slice(0, 4).map(profile => {
                  const confidence = Number(profile.confidence || 0);
                  return (
                    <span key={profile.profession} className="rounded-full border border-celestial-saturn/20 bg-celestial-saturn/10 px-3 py-1 text-[12px] font-bold text-celestial-saturn/90">
                      {recommendationCopy.profile}: {profile.profession}{confidence ? ` ${Math.round(confidence * 100)}%` : ''}
                    </span>
                  );
                })}
                {matchedWorkSignals.map(signal => (
                  <span key={signal.en} className="rounded-full border border-cyan-300/15 bg-cyan-300/8 px-3 py-1 text-[12px] font-bold text-cyan-100/70">
                    {recommendationCopy.signals}: {lang === 'zh' ? signal.zh : signal.en}
                  </span>
                ))}
                {!hasRecommendationSignals && (
                  <span className="rounded-full border border-white/8 bg-white/[0.04] px-3 py-1 text-[12px] font-bold text-white/45">
                    {recommendationCopy.noSignals}
                  </span>
                )}
              </div>
            </div>

            {recommendationError && (
              <div className="flex items-center gap-2 rounded-2xl border border-amber-400/15 bg-amber-500/10 px-4 py-3 text-xs text-amber-100/75">
                <AlertTriangle size={14} className="text-amber-300" />
                {recommendationError}
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 px-1">
                <div className="flex items-center gap-2">
                  <Star size={15} className="text-celestial-saturn" />
                  <h4 className="text-xs font-black uppercase tracking-widest text-white/65">{recommendationCopy.recommendations}</h4>
                </div>
                <span className="text-[12px] font-mono text-white/35">{recommendedSkills.length}</span>
              </div>

              {recommendedSkills.length === 0 ? (
                <div className="xf-panel p-8 text-center">
                  <p className="text-sm text-white/45">{recommendationCopy.noRecommendations}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recommendedSkills.map(({ skill, reasons }) => {
                    const availability = getDisplayAvailability(skill, installedSkills, savedKeys, lang);
                    const isInstalled = skill.installed || !!availability.installedSkill;
                    const isInstalling = installing === skill.id;
                    return (
                      <motion.div
                        key={skill.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => setDetailSkill(skill)}
                        className="xf-panel cursor-pointer p-5 transition-all hover:border-white/15 hover:bg-white/[0.06]"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl border flex shrink-0 items-center justify-center ${ICON_CLASSES[skill.icon] || 'bg-white/5 text-white/55 border-white/10'}`}>
                              {ICON_COMPONENTS[skill.icon] || <Zap size={18} />}
                            </div>
                            <div className="min-w-0">
                              <h5 className="truncate text-sm font-bold text-white/88">{skill.name}</h5>
                              <span className="text-xs font-mono text-white/45">{skill.category} &middot; {skill.toolCount || 1} {t.toolsCount || '个工具'}</span>
                            </div>
                          </div>
                          {isInstalled && (
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-500/10 px-2 py-1 text-[12px] font-bold text-green-300">
                              <CheckCircle size={10} /> {getInstallActionText(skill, true, lang, t)}
                            </span>
                          )}
                        </div>

                        <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-white/50">{skill.description}</p>
                        <div className="mt-3">
                          <SkillStatusCallout availability={availability} compact />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {reasons.slice(0, 3).map(reason => (
                            <span key={reason} className="rounded-full border border-white/8 bg-white/[0.04] px-2 py-0.5 text-[11px] font-semibold text-white/45">
                              {reason}
                            </span>
                          ))}
                        </div>
                        <div className="mt-4 flex justify-end">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isInstalled) handleInstall(skill);
                            }}
                            disabled={isInstalled || isInstalling}
                            className={`h-auto rounded-xl px-3 py-2 text-xs font-bold transition-all ${
                              isInstalled ? 'bg-green-500/20 text-green-300' : 'bg-white/10 text-white/70 hover:bg-white/18'
                            }`}
                          >
                            {isInstalled ? <><CheckCircle size={12} /> {getInstallActionText(skill, true, lang, t)}</> : isInstalling ? <><RefreshCw size={12} className="animate-spin" /> {t.installingStatus || '安装中...'}</> : <><Download size={12} /> {getInstallActionText(skill, false, lang, t)}</>}
                          </Button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex flex-col gap-1 px-1 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex items-center gap-2">
                  <Cpu size={15} className="text-cyan-200" />
                  <h4 className="text-xs font-black uppercase tracking-widest text-white/65">{recommendationCopy.installed}</h4>
                </div>
                <p className="max-w-2xl text-[12px] leading-relaxed text-white/35">{recommendationCopy.installedHint}</p>
              </div>

              {installedSkillRows.length === 0 ? (
                <div className="xf-panel p-8 text-center">
                  <p className="text-sm text-white/45">{recommendationCopy.noInstalled}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {installedSkillRows.map(({ skill, marketSkill }) => {
                    const availability = getInstalledSkillAvailability(skill, lang);
                    return (
                      <motion.div
                        key={skill.name}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => {
                          setActiveTab('installed');
                          setDetailSkill(skill);
                        }}
                        className="xf-panel cursor-pointer p-5 transition-all hover:border-white/15 hover:bg-white/[0.06]"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl border flex shrink-0 items-center justify-center ${marketSkill ? (ICON_CLASSES[marketSkill.icon] || 'bg-white/5 text-white/55 border-white/10') : 'border-cyan-300/15 bg-cyan-300/8 text-cyan-100/70'}`}>
                              {marketSkill ? (ICON_COMPONENTS[marketSkill.icon] || <Zap size={18} />) : <Cpu size={18} />}
                            </div>
                            <div className="min-w-0">
                              <h5 className="truncate text-sm font-bold text-white/88">{marketSkill?.name || skill.name}</h5>
                              <span className="text-xs font-mono text-white/45">{skill.toolCount} {t.toolsCount || '个工具'} &middot; {skill.source}</span>
                            </div>
                          </div>
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-white/8 bg-white/[0.04] px-2.5 py-1 text-[11px] font-bold text-white/50">
                            {recommendationCopy.manage}
                          </span>
                        </div>

                        <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-white/50">{marketSkill?.description || skill.description}</p>
                        <div className="mt-3">
                          <SkillStatusCallout availability={availability} compact />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {skill.connected && (
                            <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-[11px] font-bold text-green-300">{t.connected || '已连接'}</span>
                          )}
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${skill.enabled ? 'bg-green-500/10 text-green-300' : 'bg-white/5 text-white/45'}`}>
                            {skill.enabled ? (t.enabled || '已启用') : (t.disabled || '已禁用')}
                          </span>
                          {marketSkill && (
                            <span className="rounded-full bg-white/[0.04] px-2 py-0.5 text-[11px] font-bold text-white/40">{marketSkill.category}</span>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'marketplace' && (
          <motion.div key="marketplace" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }} className="space-y-6">
            {/* Install progress banner */}
            <AnimatePresence>
              {installProgress && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3 bg-celestial-saturn/10 border border-celestial-saturn/20 rounded-2xl">
                    <RefreshCw size={14} className="animate-spin text-celestial-saturn" />
                    <div>
                      <span className="text-xs font-bold text-celestial-saturn uppercase">
                        {installProgress.stage === 'downloading' ? (t.installingStatus || '下载中...') :
                         installProgress.stage === 'copying' ? (t.installingStatus || '复制文件中...') :
                         installProgress.stage === 'connecting' ? (t.connectingStage || '连接中...') :
                         (t.installingStatus || '安装中...')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {/* Filters row */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/45" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.searchSkills || '搜索技能...'} className="xf-field w-full py-2 pl-9 text-xs" />
              </div>
              {/* Category filter */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  onClick={() => setActiveCategory('')}
                  className={`rounded-xl px-3 py-1.5 text-xs font-bold transition-all ${!activeCategory ? 'bg-white/10 text-white' : 'bg-white/[0.045] text-white/55 hover:bg-white/10 hover:text-white/75'}`}
                >
                  {t.allCategories || '全部'}
                </button>
                {skillHallCategories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(activeCategory === cat ? '' : cat)}
                    className={`rounded-xl px-3 py-1.5 text-xs font-bold transition-all ${activeCategory === cat ? 'bg-celestial-saturn/20 text-celestial-saturn' : 'bg-white/[0.045] text-white/55 hover:bg-white/10 hover:text-white/75'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {/* Sort */}
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] text-white/45">{t.sortBy || '排序'}:</span>
                {(['downloads', 'rating', 'newest'] as SortKey[]).map(k => (
                  <button
                    key={k}
                    onClick={() => setSortKey(k)}
                    className={`rounded-lg px-2 py-1 text-[12px] font-bold transition-all ${sortKey === k ? 'bg-white/10 text-white' : 'text-white/45 hover:bg-white/[0.045] hover:text-white/70'}`}
                  >
                    {(t as any)[k] || k}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence>
                {sortedMarket.map(skill => {
                  const availability = getDisplayAvailability(skill, installedSkills, savedKeys, lang);
                  const runtimeInstalled = !!availability.installedSkill;
                  const isInstalled = skill.installed || runtimeInstalled;
                  return (
                  <motion.div
                    key={skill.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setDetailSkill(skill)}
                    className="xf-panel cursor-pointer p-6 transition-all hover:border-white/15 hover:bg-white/[0.06]"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${ICON_CLASSES[skill.icon] || 'bg-white/5 text-white/55 border-white/10'}`}>
                          {ICON_COMPONENTS[skill.icon] || <Zap size={18} />}
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-bold text-white/90 text-sm">{skill.name}</h4>
                          <span className="text-xs text-white/55 font-mono">{t.version || '版本'} {skill.version || '1.0'} &middot; {skill.toolCount || 1} {t.toolsCount || '个工具'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {skill.requiresApiKey && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-full text-xs font-bold uppercase text-amber-400" title={(t.requiresApiKey || '需要') + ' ' + skill.apiKeyEnv + ' API key'}>
                            <Key size={8} /> API
                          </span>
                        )}
                        {skill.requiresSetup && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/10 rounded-full text-xs font-bold uppercase text-purple-400" title={skill.setupNote || (t.setupRequired || '需要手动配置')}>
                            <Wrench size={8} /> Setup
                          </span>
                        )}
                        {isInstalled && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-green-500/10 rounded-full text-[12px] font-bold uppercase text-green-400">
                            <CheckCircle size={10} /> {getInstallActionText(skill, true, lang, t)}
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-white/50 leading-relaxed mb-4 line-clamp-2">{skill.description}</p>
                    <div className="mb-4">
                      <SkillStatusCallout availability={availability} />
                    </div>

                    {/* Setup note */}
                    {skill.requiresSetup && skill.setupNote && (
                      <div className="mb-3 p-2 bg-purple-500/5 border border-purple-500/10 rounded-xl">
                        <p className="text-[12px] text-purple-300/70 leading-relaxed">{skill.setupNote}</p>
                      </div>
                    )}
                    {skill.requiresApiKey && skill.apiKeyUrl && (
                      <div className="mb-3 p-2 bg-amber-500/5 border border-amber-500/10 rounded-xl">
                        <p className="text-[12px] text-amber-300/70 leading-relaxed">
                          Requires <strong>{skill.apiKeyEnv}</strong> — <a href={skill.apiKeyUrl} target="_blank" rel="noopener" className="underline hover:text-amber-200">{t.getApiKey || '获取 API 密钥'}</a>
                        </p>
                      </div>
                    )}

                    {/* Tags */}
                    <div className="flex items-center gap-2 flex-wrap mb-4">
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-white/5 rounded-full text-[12px] text-white/55">
                        <Tag size={8} /> {skill.category}
                      </span>
                      <span className="text-[12px] text-white/45">{skill.author}</span>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1 text-xs text-white/45">
                          <Download size={10} /> {skill.downloads.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-white/45">
                          <Star size={10} className="text-amber-400" fill="currentColor" /> {skill.rating > 0 ? skill.rating.toFixed(1) : '--'}
                        </span>
                      </div>
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleInstall(skill); }}
                        disabled={isInstalled || installing === skill.id}
                        className={`text-xs font-bold px-3 py-1.5 h-auto rounded-xl transition-all ${
                          isInstalled
                            ? 'bg-green-500/20 text-green-400 cursor-default'
                            : installing === skill.id
                              ? 'bg-celestial-saturn/50 text-black cursor-wait'
                              : 'bg-celestial-saturn text-black hover:scale-105'
                        }`}
                      >
                        {isInstalled ? (
                          <><CheckCircle size={12} className="mr-1" /> {getInstallActionText(skill, true, lang, t)}</>
                        ) : installing === skill.id ? (
                          <><RefreshCw size={12} className="mr-1 animate-spin" /> {t.installingStatus || '安装中...'}</>
                        ) : (
                          <>{isExternalRuntimeSkill(skill) ? <ExternalLink size={12} className="mr-1" /> : <Download size={12} className="mr-1" />} {getInstallActionText(skill, false, lang, t)}</>
                        )}
                      </Button>
                    </div>
                  </motion.div>
                );})}
              </AnimatePresence>
            </div>
            {sortedMarket.length === 0 && (
              <div className="xf-panel p-16 text-center">
                <ShoppingBag size={40} className="text-white/45 mx-auto mb-4" />
                <p className="text-white/40 font-bold uppercase tracking-widest text-sm">{t.noSkillsFound || '未找到技能'}</p>
                <p className="text-white/45 text-xs mt-2">{search || activeCategory ? (t.skillNoResultsFilter || '无结果。试试其他筛选。') : (t.skillCheckBack || '敬请期待，或自己动手生成一个。')}</p>
              </div>
            )}

            {/* External search results: npm + GitHub */}
            <AnimatePresence>
              {searchingExternal && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-2 text-xs text-white/45 py-2">
                  <RefreshCw size={12} className="animate-spin" /> Searching npm & GitHub...
                </motion.div>
              )}

              {externalError && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-xs text-red-300/80 bg-red-500/10 border border-red-500/15 rounded-xl px-3 py-2">
                  {externalError}
                </motion.div>
              )}

              {/* npm registry results */}
              {npmResults.length > 0 && (
                <motion.div key="npm-section" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-2">
                  <div className="flex items-center gap-2 px-1">
                    <span className="text-xs font-bold uppercase text-red-400 tracking-wider">npm Registry</span>
                    <span className="text-[12px] text-white/45 font-mono">{npmResults.length} found</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {npmResults.map(skill => {
                      const isInstalling = installing === skill.id;
                      return (
                        <div key={skill.id}
                          className="p-4 bg-red-500/5 rounded-2xl border border-red-500/10 hover:border-red-500/20 transition-all flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                            <Download size={14} className="text-red-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-xs font-bold text-white/80 truncate">{skill.name}</h5>
                            <p className="text-xs text-white/55 line-clamp-1">{skill.description || skill.version || ''}</p>
                          </div>
                          <Button
                            onClick={() => handleInstall(skill)}
                            disabled={isInstalling}
                            className="text-xs font-bold px-3 py-1.5 h-auto rounded-lg shrink-0 bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
                          >
                            {isInstalling ? <RefreshCw size={11} className="animate-spin" /> : t.installBtn || '安装'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* GitHub results */}
              {githubResults.length > 0 && (
                <motion.div key="github-section" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-2">
                  <div className="flex items-center gap-2 px-1">
                    <Globe size={11} className="text-purple-400" />
                    <span className="text-xs font-bold uppercase text-purple-400 tracking-wider">GitHub</span>
                    <span className="text-[12px] text-white/45 font-mono">{githubResults.length} repos</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {githubResults.map(repo => {
                      const isInstalling = installing === repo.id;
                      return (
                        <div key={repo.id}
                          className="p-4 bg-purple-500/5 rounded-2xl border border-purple-500/10 hover:border-purple-500/20 transition-all flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                            <Star size={14} className="text-purple-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-xs font-bold text-white/80 truncate">{repo.name}</h5>
                            <p className="text-xs text-white/55 line-clamp-1">
                              {repo.stars ? `★ ${repo.stars} · ` : ''}{repo.description || repo.language || ''}
                            </p>
                          </div>
                          <Button
                            onClick={() => handleInstall(repo)}
                            disabled={isInstalling}
                            className="text-xs font-bold px-3 py-1.5 h-auto rounded-lg shrink-0 bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-all"
                          >
                            {isInstalling ? <RefreshCw size={11} className="animate-spin" /> : t.installBtn || '安装'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {activeTab === 'mcp' && (
          <motion.div key="mcp" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }} className="overflow-hidden rounded-3xl border border-white/5 bg-white/[0.03]">
            <MCPSettings t={t} />
          </motion.div>
        )}

        {activeTab === 'installed' && (
          <motion.div key="installed" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }} className="space-y-6">
            <div className="xf-panel p-5">
              <div className="flex items-center gap-2">
                <Cpu size={17} className="text-cyan-200" />
                <h4 className="text-sm font-black uppercase tracking-normal text-white/85">{recommendationCopy.managementTitle}</h4>
              </div>
              <p className="mt-2 max-w-3xl text-xs leading-relaxed text-white/45">{recommendationCopy.managementHint}</p>
            </div>
            {brokenSkillCount > 0 && (
              <div className="flex items-center justify-between gap-3 rounded-2xl border border-red-500/10 bg-red-500/5 p-4">
                <div className="flex min-w-0 items-center gap-3">
                  <AlertTriangle size={16} className="shrink-0 text-red-300" />
                  <p className="text-xs font-bold text-red-200">
                    {lang === 'zh' ? `${brokenSkillCount} 个技能包不完整` : `${brokenSkillCount} incomplete skill package${brokenSkillCount > 1 ? 's' : ''}`}
                  </p>
                </div>
                <button
                  onClick={handleCleanupBroken}
                  disabled={cleaningBroken}
                  className="flex shrink-0 items-center gap-1.5 rounded-xl bg-red-500/10 px-3 py-2 text-xs font-bold text-red-200 transition-colors hover:bg-red-500/20 disabled:opacity-40"
                >
                  {cleaningBroken ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  {lang === 'zh' ? '清理坏技能' : 'Clean Broken'}
                </button>
              </div>
            )}
            {loading ? (
              <div className="xf-panel p-16 text-center">
                <p className="text-white/40 text-sm">{t.loading || '加载中...'}</p>
              </div>
            ) : installedSkills.length === 0 ? (
              <div className="xf-panel p-16 text-center">
                <Wrench size={40} className="text-white/45 mx-auto mb-4" />
                <p className="text-white/40 font-bold uppercase tracking-widest text-sm">{t.noSkillsInstalled || '未安装技能'}</p>
                <p className="text-white/45 text-xs mt-2">{t.skillBrowseMarketplace || '浏览市场或生成一个。'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AnimatePresence>
                  {installedSkills.map(skill => {
                    const availability = getInstalledSkillAvailability(skill, lang);
                    return (
                    <motion.div
                      key={skill.name}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setDetailSkill(skill)}
                      className="xf-panel cursor-pointer p-6 transition-all hover:border-white/15 hover:bg-white/[0.06]"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-white/90 text-sm">{skill.name}</h4>
                          <span className="text-xs text-white/55 font-mono">{skill.toolCount} {t.toolsCount || '个工具'}{skill.installedAt ? ` · ${new Date(skill.installedAt).toLocaleDateString()}` : ''}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {shouldOfferRepair(skill) && (
                            <button onClick={(e) => { e.stopPropagation(); handleRepair(skill.name); }}
                              disabled={repairing === skill.name}
                              className="p-2 rounded-lg text-amber-300 transition-colors hover:bg-amber-500/10 disabled:opacity-40"
                              title={lang === 'zh' ? '修复技能' : 'Repair'}
                            >
                              <RefreshCw size={13} className={repairing === skill.name ? 'animate-spin' : ''} />
                            </button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); handleToggle(skill.name, !skill.enabled); }}
                            disabled={skill.broken || repairing === skill.name}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-35 disabled:cursor-not-allowed ${skill.enabled ? 'text-green-400 hover:bg-green-500/10' : 'text-white/45 hover:bg-white/5'}`}
                            title={skill.enabled ? (t.skillDisableBtn || '禁用') : (t.skillEnableBtn || '启用')}
                          >
                            {skill.enabled ? <Power size={13} /> : <PowerOff size={13} />}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handlePublish(skill.name); }}
                            className="p-2 hover:bg-violet-500/10 rounded-lg text-white/45 hover:text-violet-400 transition-colors"
                            title={t.publishBtn || '发布'}
                          >
                            <Upload size={13} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleUninstall(skill.name); }}
                            className="p-2 hover:bg-red-500/10 rounded-lg text-white/45 hover:text-red-400 transition-colors"
                            title={t.uninstallBtn || '卸载'}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>

                      <p className="text-xs text-white/50 leading-relaxed mb-4 line-clamp-2">{skill.description}</p>
                      <div className="mb-4">
                        <SkillStatusCallout availability={availability} compact />
                      </div>

                      {/* Tags */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {skill.autoGenerated && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 rounded-full text-[12px] text-emerald-400 font-bold">{t.autoGenerated || '自动'}</span>
                        )}
                        {skill.connected && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/10 rounded-full text-[12px] text-green-400 font-bold">{t.connected || '已连接'}</span>
                        )}
                        {skill.broken && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/10 rounded-full text-[12px] text-red-300 font-bold">
                            <AlertTriangle size={11} />{lang === 'zh' ? '坏技能' : 'Broken'}
                          </span>
                        )}
                        {!skill.broken && skill.startupError && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-full text-[12px] text-amber-300 font-bold">
                            <AlertTriangle size={11} />{lang === 'zh' ? '需修复' : 'Needs repair'}
                          </span>
                        )}
                        {!skill.broken && skill.enabled && !skill.connected && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-full text-[12px] text-amber-300 font-bold">
                            <AlertTriangle size={11} />{lang === 'zh' ? '未连接' : 'Not connected'}
                          </span>
                        )}
                        {!skill.broken && skill.healthStatus && !skill.enabled && !['connected', 'disconnected', 'unknown'].includes(skill.healthStatus) && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-full text-[12px] text-amber-300 font-bold">
                            <AlertTriangle size={11} />{skill.healthStatus}
                          </span>
                        )}
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold ${skill.enabled ? 'bg-green-500/10 text-green-400' : 'bg-white/5 text-white/45'}`}>
                          {skill.enabled ? (t.enabled || '已启用') : (t.disabled || '已禁用')}
                        </span>
                      </div>
                    </motion.div>
                  );})}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'generate' && (
          <motion.div key="generate" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }} className="space-y-6">
            <div className="xf-panel p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Sparkles className="text-celestial-saturn" size={18} />
                <h4 className="text-sm font-bold uppercase tracking-tight text-white">{t.generateNewSkill || '生成一个新技能'}</h4>
              </div>
              <p className="text-xs text-white/55 leading-relaxed">
                {t.skillGenerateDesc || 'Describe what the skill should do. 小枫 generates a complete MCP server package.'}
              </p>
              <div className="flex gap-2">
                <Input value={genDescription} onChange={e => setGenDescription(e.target.value)}
                  placeholder={t.skillGeneratePlaceholder || '例如：检查网站是否宕机...'}
                  className="xf-field flex-1 py-2 text-xs"
                  onKeyDown={e => e.key === 'Enter' && handleGenerate()} />
                <Button onClick={handleGenerate} disabled={generating || !genDescription.trim()}
                  className="xf-button-primary bg-celestial-saturn text-black hover:bg-celestial-saturn/90">
                  {generating ? (t.generating || '生成中...') : (t.generateBtn || '生成')}
                </Button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {SAMPLE_PROMPTS.map(ex => (
                  <button key={ex} onClick={() => setGenDescription(ex)} className="rounded-lg border border-white/[0.08] bg-white/[0.045] px-2 py-1 text-[12px] text-white/55 transition-colors hover:border-white/15 hover:text-white/75">
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}


      </AnimatePresence>
      <AnimatePresence>
        {详情Skill && (
          <SkillDetailPane 详情Skill={详情Skill} setDetailSkill={setDetailSkill} t={t} lang={lang} marketSkills={skillHallMarketSkills} installedSkills={installedSkills} installing={installing} repairing={repairing} savedKeys={savedKeys} keyInputs={keyInputs} setKeyInputs={setKeyInputs} savingKey={savingKey} handleInstall={handleInstall} handleSaveKey={handleSaveKey} handleToggle={handleToggle} handleUninstall={handleUninstall} handleRepair={handleRepair} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Rating & Reviews Section ──
function SkillRatingSection({ skill, t, lang }: { skill: any; t: any; lang: string }) {
  const isZh = lang !== 'en';
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviews, setShowReviews] = useState(false);

  useEffect(() => {
    if (showReviews && reviews.length === 0) {
      setReviewsLoading(true);
      apiFetch(`/api/marketplace/skills/${skill.id}/reviews`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => setReviews(data.reviews || data || []))
        .catch(() => {})
        .finally(() => setReviewsLoading(false));
    }
  }, [showReviews, skill.id]);

  const handleRate = async (value: number) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/marketplace/skills/${skill.id}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: value }),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setRating(value);
        toast.success(isZh ? '评分已提交' : 'Rating submitted');
      } else {
        toast.error(data.error || (isZh ? '评分失败' : 'Rating failed'));
      }
    } catch {
      toast.error(isZh ? '请求失败' : 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Star rating input */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-white/50 uppercase tracking-widest">{isZh ? '评分' : 'RATE'}</span>
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              disabled={submitting}
              onClick={() => handleRate(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="transition-colors disabled:cursor-wait"
            >
              <Star
                size={16}
                className={star <= (hoverRating || rating) ? 'text-amber-400' : 'text-white/15'}
                fill={star <= (hoverRating || rating) ? 'currentColor' : 'none'}
              />
            </button>
          ))}
        </div>
        {rating > 0 && <span className="text-xs text-amber-400 font-bold">{rating}/5</span>}
      </div>

      {/* View reviews toggle */}
      <button
        onClick={() => setShowReviews(!showReviews)}
        className="text-xs font-bold text-blue-400/70 hover:text-blue-400 transition-colors"
      >
        {showReviews ? (isZh ? '收起评价' : 'Hide reviews') : (isZh ? '查看评价' : 'View reviews')}
        {reviews.length > 0 && <span className="ml-1 text-white/30">({reviews.length})</span>}
      </button>

      {/* Reviews list */}
      {showReviews && (
        <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
          {reviewsLoading ? (
            <Loader2 size={16} className="animate-spin text-white/30 mx-auto" />
          ) : reviews.length === 0 ? (
            <span className="text-xs text-white/30">{isZh ? '暂无评价' : 'No reviews yet'}</span>
          ) : (
            reviews.map((r: any, i: number) => (
              <div key={r.id || i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} size={10} className={s <= (r.rating || 0) ? 'text-amber-400' : 'text-white/10'} fill={s <= (r.rating || 0) ? 'currentColor' : 'none'} />
                    ))}
                  </div>
                  {r.user && <span className="text-[10px] text-white/40">{r.user}</span>}
                  {r.date && <span className="text-[10px] text-white/25 ml-auto">{new Date(r.date).toLocaleDateString()}</span>}
                </div>
                {r.comment && <p className="text-xs text-white/50">{r.comment}</p>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SkillDetailPane({ 详情Skill, setDetailSkill, t, lang, marketSkills, installedSkills, installing, repairing, savedKeys, keyInputs, setKeyInputs, savingKey, handleInstall, handleSaveKey, handleToggle, handleUninstall, handleRepair }: {
  详情Skill: any;
  setDetailSkill: (v: any) => void;
  t: any;
  lang: 'en' | 'zh';
  marketSkills: any[];
  installedSkills: InstalledSkill[];
  installing: string | null;
  repairing: string | null;
  savedKeys: Record<string, boolean>;
  keyInputs: Record<string, string>;
  setKeyInputs: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  savingKey: string | null;
  handleInstall: (skill: any) => void;
  handleSaveKey: (envKey: string, value: string) => void;
  handleToggle: (name: string, enabled: boolean) => void;
  handleUninstall: (name: string) => void;
  handleRepair: (name: string) => void;
}) {
  const isMarketSkill = 'id' in 详情Skill;
  const mSkill: any | undefined = isMarketSkill
    ? 详情Skill
    : marketSkills.find(s => s.id === `skill-${详情Skill.name}`);
  const marketAvailability = mSkill
    ? getDisplayAvailability(mSkill, installedSkills, savedKeys, lang)
    : undefined;
  const installedAvailability = !mSkill && !isMarketSkill && 详情Skill
    ? getInstalledSkillAvailability(详情Skill, lang)
    : undefined;
  const resolvedInstalledSkill = marketAvailability?.installedSkill;
  const marketInstalled = Boolean(mSkill?.installed || resolvedInstalledSkill);
  const 详情RuntimeIssue = !isMarketSkill && (
    !!详情Skill.broken ||
    !!详情Skill.startupError ||
    (!!详情Skill.healthStatus && ['crashed', 'failed', 'restarting'].includes(详情Skill.healthStatus)) ||
    (!!详情Skill.enabled && !详情Skill.connected)
  );
  return (
    <motion.div
      key="skill-详情-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => setDetailSkill(null)}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm"
    >
      <motion.div
        key={(详情Skill as any).id || (详情Skill as any).name || '详情'}
        initial={{ opacity: 0, y: 18, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={{ duration: 0.16 }}
        onClick={(event) => event.stopPropagation()}
        className="xf-panel max-h-[min(760px,calc(100vh-48px))] w-full max-w-3xl overflow-y-auto p-6 space-y-5 shadow-2xl"
      >
        <button
          onClick={() => setDetailSkill(null)}
          className="flex items-center gap-2 text-xs font-bold text-white/45 hover:text-white/70 transition-colors"
        >
          <X size={14} /> {t.close || '关闭'}
        </button>

      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl border flex items-center justify-center shrink-0 ${
          mSkill ? ICON_CLASSES[mSkill.icon] || 'bg-white/5 text-white/55 border-white/10' : 'bg-white/5 text-white/55 border-white/10'
        }`}>
          {mSkill ? (ICON_COMPONENTS[mSkill.icon] || <Zap size={22} />) : <Zap size={22} />}
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">
            {mSkill?.name ?? 详情Skill.name}
          </h3>
          {mSkill && (
            <span className="text-xs text-white/55 font-mono">
              v {mSkill.version || '1.0'} &middot; {mSkill.toolCount || 1} tools
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-white/45">
        {mSkill && (
          <>
            <span className="flex items-center gap-1"><Tag size={10} /> {mSkill.category}</span>
            <span>{mSkill.author}</span>
            <span className="flex items-center gap-1"><Download size={10} /> {mSkill.downloads.toLocaleString()}</span>
            <span className="flex items-center gap-1"><Star size={10} className="text-amber-400" fill="currentColor" /> {mSkill.rating > 0 ? mSkill.rating.toFixed(1) : '--'}</span>
          </>
        )}
        {!isMarketSkill && 详情Skill && (
          <>
            <span>{详情Skill.toolCount} tools</span>
            {详情Skill.installedAt && (
              <span>Installed {new Date(详情Skill.installedAt).toLocaleDateString()}</span>
            )}
            <span>{详情Skill.source}</span>
          </>
        )}
      </div>

      <p className="text-sm text-white/60 leading-relaxed">
        {mSkill?.description ?? 详情Skill.description}
      </p>

      {/* Rating & Reviews (marketplace skills only) */}
      {isMarketSkill && mSkill && (
        <SkillRatingSection skill={mSkill} t={t} lang={lang} />
      )}

      {marketAvailability && <SkillStatusCallout availability={marketAvailability} />}
      {!marketAvailability && installedAvailability && <SkillStatusCallout availability={installedAvailability} />}

      {mSkill?.runtime === 'external' && (
        <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-2xl space-y-2">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase text-cyan-400">
            <ExternalLink size={12} /> External Runtime
          </span>
          {mSkill.externalCommand && (
            <p className="text-sm font-mono text-cyan-300/70 break-all">{mSkill.externalCommand}</p>
          )}
          <p className="text-[12px] text-cyan-400/60">
            {lang === 'zh' ? '它会作为 Team 里的外部 Agent 运行，通过本机 CLI 连接，测试通过后再参与调度。' : 'This skill runs as a Team external agent through a local CLI and joins orchestration after a passing test.'}
          </p>
        </div>
      )}

      {mSkill?.requiresSetup && mSkill.setupNote && (
        <div className="p-4 bg-purple-500/5 border border-purple-500/10 rounded-2xl">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase text-purple-400 mb-2">
            <Wrench size={12} /> Setup Required
          </span>
          <p className="text-sm text-purple-200/70 leading-relaxed">{mSkill.setupNote}</p>
        </div>
      )}

      {mSkill?.requiresApiKey && mSkill.apiKeyEnv && (
        <div className="p-4 bg-amber-500/5 border border-amber-500/10 rounded-2xl space-y-3">
          <div className="flex items-center gap-1.5">
            <Key size={12} className="text-amber-400" />
            <span className="text-xs font-bold uppercase text-amber-400">API Key Required</span>
          </div>
          <p className="text-sm text-amber-300/70">
            Requires <strong>{mSkill.apiKeyEnv}</strong>
            {mSkill.apiKeyUrl && (
              <> &mdash; <a href={mSkill.apiKeyUrl} target="_blank" rel="noopener" className="underline hover:text-amber-200">get API key</a></>
            )}
          </p>
          {savedKeys[mSkill.apiKeyEnv] ? (
            <span className="flex items-center gap-1 text-sm text-green-400"><CheckCircle size={14} /> {t.keyConfigured || '密钥已配置'}</span>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={keyInputs[mSkill.apiKeyEnv] || ''}
                onChange={e => setKeyInputs(prev => ({ ...prev, [mSkill.apiKeyEnv]: e.target.value }))}
                placeholder="sk-..."
                className="flex-1 px-3 py-2 bg-white/10 border border-amber-500/20 rounded-xl text-white text-sm placeholder:text-white/35 focus:outline-none focus:border-amber-500/50"
              />
              <Button
                onClick={() => handleSaveKey(mSkill.apiKeyEnv!, keyInputs[mSkill.apiKeyEnv!] || '')}
                disabled={savingKey === mSkill.apiKeyEnv || !keyInputs[mSkill.apiKeyEnv!]?.trim()}
                className="shrink-0 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-bold rounded-xl disabled:opacity-40"
              >
                {savingKey === mSkill.apiKeyEnv ? <RefreshCw size={12} className="animate-spin" /> : 'Save'}
              </Button>
            </div>
          )}
        </div>
      )}

      {mSkill && (mSkill.npmPackage || mSkill.repoUrl) && (
        <div className="flex items-center gap-3 text-xs">
          {mSkill.npmPackage && (
            <a href={`https://www.npmjs.com/package/${mSkill.npmPackage}`} target="_blank" rel="noopener" className="flex items-center gap-1 text-cyan-400 hover:underline">
              <ExternalLink size={10} /> npm
            </a>
          )}
          {mSkill.repoUrl && (
            <a href={mSkill.repoUrl} target="_blank" rel="noopener" className="flex items-center gap-1 text-purple-400 hover:underline">
              <ExternalLink size={10} /> GitHub
            </a>
          )}
        </div>
      )}

      {!isMarketSkill && 详情Skill && (
        <div className="flex items-center gap-2 flex-wrap">
          {详情Skill.autoGenerated && (
            <span className="px-2 py-0.5 bg-emerald-500/10 rounded-full text-xs text-emerald-400 font-bold">{t.autoGenerated || '自动生成'}</span>
          )}
          {详情Skill.connected && (
            <span className="px-2 py-0.5 bg-green-500/10 rounded-full text-xs text-green-400 font-bold">{t.connected || '已连接'}</span>
          )}
          {详情Skill.broken && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/10 rounded-full text-xs text-red-300 font-bold">
              <AlertTriangle size={11} />{lang === 'zh' ? '坏技能' : 'Broken'}
            </span>
          )}
          {!详情Skill.broken && 详情Skill.startupError && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-full text-xs text-amber-300 font-bold">
              <AlertTriangle size={11} />{lang === 'zh' ? '需修复' : 'Needs repair'}
            </span>
          )}
          {!详情Skill.broken && 详情Skill.enabled && !详情Skill.connected && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-full text-xs text-amber-300 font-bold">
              <AlertTriangle size={11} />{lang === 'zh' ? '未连接' : 'Not connected'}
            </span>
          )}
          {!详情Skill.broken && 详情Skill.healthStatus && !['connected', 'disconnected', 'unknown'].includes(详情Skill.healthStatus) && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-full text-xs text-amber-300 font-bold">
              <AlertTriangle size={11} />{详情Skill.healthStatus}
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
            详情Skill.enabled ? 'bg-green-500/10 text-green-400' : 'bg-white/5 text-white/45'
          }`}>
            {详情Skill.enabled ? (t.enabled || '已启用') : (t.disabled || '已禁用')}
          </span>
        </div>
      )}

      {!isMarketSkill && 详情Skill?.broken && (
        <div className="rounded-2xl border border-red-500/10 bg-red-500/5 p-4 text-xs leading-relaxed text-red-100/75">
          {lang === 'zh'
            ? '这个技能包缺少可启动入口。小枫 会先尝试从 npm、GitHub 或内置技能源重装；没有来源时需要清理后重新安装或重新生成。'
            : 'This skill package has no runnable entry. 小枫 will try npm, GitHub, or bundled reinstall sources first; otherwise clean it up and reinstall or regenerate it.'}
        </div>
      )}

      <div className="flex items-center gap-3 pt-2 border-t border-white/5">
        {mSkill && isMarketSkill ? (
          <Button
            onClick={() => { handleInstall(mSkill); setDetailSkill(null); }}
            disabled={marketInstalled || installing === mSkill.id}
            className={`text-xs font-bold px-5 py-2.5 h-auto rounded-xl ${
              marketInstalled
                ? 'bg-green-500/20 text-green-400 cursor-default'
                : 'bg-celestial-saturn text-black hover:scale-105'
            }`}
          >
            {marketInstalled ? (
              <><CheckCircle size={14} className="mr-1" /> {getInstallActionText(mSkill, true, lang, t)}</>
            ) : installing === mSkill.id ? (
              <><RefreshCw size={14} className="mr-1 animate-spin" /> {t.installingStatus || '安装中...'}</>
            ) : (
              <>{isExternalRuntimeSkill(mSkill) ? <ExternalLink size={14} className="mr-1" /> : <Download size={14} className="mr-1" />} {getInstallActionText(mSkill, false, lang, t)}</>
            )}
          </Button>
        ) : 详情Skill ? (
          <>
            {详情RuntimeIssue && (
              <button
                onClick={() => handleRepair(详情Skill.name)}
                disabled={repairing === 详情Skill.name}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 transition-colors disabled:opacity-40"
              >
                <RefreshCw size={13} className={repairing === 详情Skill.name ? 'animate-spin' : ''} />
                {lang === 'zh' ? '修复' : 'Repair'}
              </button>
            )}
            <button
              onClick={() => handleToggle(详情Skill.name, !详情Skill.enabled)}
              disabled={详情Skill.broken || repairing === 详情Skill.name}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                详情Skill.enabled
                  ? 'text-white/55 hover:bg-white/5'
                  : 'text-green-400 bg-green-500/10 hover:bg-green-500/20'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {详情Skill.enabled ? <PowerOff size={13} /> : <Power size={13} />}
              {详情Skill.enabled ? 'Disable' : 'Enable'}
            </button>
            <button
              onClick={() => { handleUninstall(详情Skill.name); setDetailSkill(null); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors"
            >
              <Trash2 size={13} /> Uninstall
            </button>
          </>
        ) : null}
      </div>
      </motion.div>
    </motion.div>
  );
}
